// Generated by ReScript, PLEASE EDIT WITH CARE

import * as Rxjs from "rxjs";
import * as LogUtils from "../utils/LogUtils.bs.jsx";
import * as Reactfire from "reactfire";
import * as ObjectUtils from "../utils/ObjectUtils.bs.jsx";
import * as Stdlib_Dict from "@rescript/runtime/lib/es6/Stdlib_Dict.js";
import * as Stdlib_Null from "@rescript/runtime/lib/es6/Stdlib_Null.js";
import * as Stdlib_Array from "@rescript/runtime/lib/es6/Stdlib_Array.js";
import * as Stdlib_Float from "@rescript/runtime/lib/es6/Stdlib_Float.js";
import * as Stdlib_Option from "@rescript/runtime/lib/es6/Stdlib_Option.js";
import * as Stdlib_JsError from "@rescript/runtime/lib/es6/Stdlib_JsError.js";
import * as Primitive_option from "@rescript/runtime/lib/es6/Primitive_option.js";
import * as Firestore from "rxfire/firestore";
import * as Firestore$1 from "firebase/firestore";
import * as Primitive_exceptions from "@rescript/runtime/lib/es6/Primitive_exceptions.js";

function kegDoc(firestore, placeId, kegId) {
  return Firestore$1.doc(firestore, `places/` + placeId + `/kegs/` + kegId);
}

function placeCollection(firestore) {
  return Firestore$1.collection(firestore, "places");
}

function personsIndexDocument(firestore, placeId) {
  return Firestore$1.doc(firestore, `places/` + placeId + `/personsIndex/1`);
}

function placeDocument(firestore, placeId) {
  return Firestore$1.doc(firestore, `places/` + placeId);
}

function placePersonsCollection(firestore, placeId) {
  return Firestore$1.collection(firestore, `places/` + placeId + `/persons`);
}

function placePersonDocument(firestore, placeId, personId) {
  return Firestore$1.doc(firestore, `places/` + placeId + `/persons/` + personId);
}

function placeKegsCollection(firestore, placeId) {
  return Firestore$1.collection(firestore, `places/` + placeId + `/kegs`);
}

function placeKegDocument(firestore, placeId, kegId) {
  return Firestore$1.doc(firestore, `places/` + placeId + `/kegs/` + kegId);
}

function webAuthnUsersDocument(firestore, userId) {
  return Firestore$1.doc(firestore, `webAuthnUsers/` + userId);
}

let removeLastUndefined = (tuple => tuple.at(-1) === undefined ? tuple.slice(0, -1) : tuple);

function personsAllRecordToTuple(param) {
  return removeLastUndefined([
    param.name,
    param.recentActivityAt,
    param.balance,
    param.userId,
    param.preferredTap
  ]);
}

function personsAllTupleToRecord(param) {
  return {
    balance: param[2],
    name: param[0],
    preferredTap: param[4],
    recentActivityAt: param[1],
    userId: param[3]
  };
}

function isPersonActive(p) {
  return p.preferredTap !== undefined;
}

function personsIndexConverter_fromFirestore(snapshot, options) {
  let match = snapshot.data(options);
  let allWithRecord = Stdlib_Dict.mapValues(match.all, personsAllTupleToRecord);
  return {
    all: allWithRecord
  };
}

function personsIndexConverter_toFirestore(place, param) {
  let allWithTuple = Stdlib_Dict.mapValues(place.all, personsAllRecordToTuple);
  return {
    all: allWithTuple
  };
}

let personsIndexConverter = {
  fromFirestore: personsIndexConverter_fromFirestore,
  toFirestore: personsIndexConverter_toFirestore
};

function personsIndexConverted(firestore, placeId) {
  return personsIndexDocument(firestore, placeId).withConverter(personsIndexConverter);
}

function formatKegSerial(serial) {
  return "#" + serial.toString().padStart(3, "0");
}

function kegConverter_fromFirestore(snapshot, options) {
  let keg = snapshot.data(options);
  let consumptionsSum = Stdlib_Array.reduce(Object.values(keg.consumptions), 0, (sum, consumption) => sum + consumption.milliliters | 0);
  let serialFormatted = formatKegSerial(keg.serial);
  return {
    beer: keg.beer,
    consumptions: keg.consumptions,
    consumptionsSum: consumptionsSum,
    createdAt: keg.createdAt,
    donors: keg.donors,
    depletedAt: keg.depletedAt,
    milliliters: keg.milliliters,
    price: keg.price,
    recentConsumptionAt: keg.recentConsumptionAt,
    serial: keg.serial,
    serialFormatted: serialFormatted
  };
}

function kegConverter_toFirestore(keg, param) {
  return {
    beer: keg.beer,
    consumptions: keg.consumptions,
    createdAt: keg.createdAt,
    depletedAt: keg.depletedAt,
    donors: keg.donors,
    milliliters: keg.milliliters,
    price: keg.price,
    recentConsumptionAt: keg.recentConsumptionAt,
    serial: keg.serial
  };
}

let kegConverter = {
  fromFirestore: kegConverter_fromFirestore,
  toFirestore: kegConverter_toFirestore
};

function placeKegsCollectionConverted(firestore, placeId) {
  return placeKegsCollection(firestore, placeId).withConverter(kegConverter);
}

let getUid = (data => data?.uid);

function placesByUserIdRx(firestore, userId) {
  return Firestore.collectionData(Firestore$1.query(Firestore$1.collection(firestore, "places"), Firestore$1.where(`accounts.` + userId, "!=", null)), { "idField": "uid" });
}

let slidingWindowInMillis = 12 * 60 * 60 * 1000;

let slidingWindowRx = Rxjs.interval(3600000).pipe(Rxjs.startWith(0), Rxjs.map((param, param$1) => {
  let slidingWindow = new Date();
  slidingWindow.setHours(slidingWindow.getHours() - 12 | 0, 0, 0, 0);
  return Firestore$1.Timestamp.fromDate(slidingWindow);
}), Rxjs.shareReplay(1));

function recentlyFinishedKegsRx(firestore, placeId) {
  return slidingWindowRx.pipe(Rxjs.switchMap(slidingWindow => Firestore.collectionData(Firestore$1.query(placeKegsCollectionConverted(firestore, placeId), Firestore$1.where("depletedAt", ">=", slidingWindow)), { "idField": "uid" })));
}

function kegFirstConsumptionTimestamp(keg) {
  return Stdlib_Option.flatMap(Stdlib_Array.reduce(Object.keys(keg.consumptions), undefined, (min, timestampStr) => {
    if (min !== undefined && timestampStr.localeCompare(min) >= 0.0) {
      return min;
    } else {
      return timestampStr;
    }
  }), Stdlib_Float.fromString);
}

function groupKegConsumptionsByUser(targetOpt, keg) {
  let target = targetOpt !== undefined ? targetOpt : new Map();
  Object.entries(keg.consumptions).forEach(param => {
    let consumption = param[1];
    let timestampStr = param[0];
    let userCons_kegId = getUid(keg);
    let userCons_beer = keg.beer;
    let userCons_milliliters = consumption.milliliters;
    let userCons_createdAt = new Date(Stdlib_Option.getOrThrow(Stdlib_Float.fromString(timestampStr), undefined));
    let userCons = {
      consumptionId: timestampStr,
      kegId: userCons_kegId,
      beer: userCons_beer,
      milliliters: userCons_milliliters,
      createdAt: userCons_createdAt
    };
    let consumptions = target.get(consumption.person.id);
    if (consumptions !== undefined) {
      consumptions.push(userCons);
    } else {
      target.set(consumption.person.id, [userCons]);
    }
  });
  return target;
}

function allChargedKegsRx(firestore, placeId) {
  let chargedKegsQuery = Firestore$1.query(placeKegsCollectionConverted(firestore, placeId), Firestore$1.where("depletedAt", "==", null), Firestore$1.limit(50));
  return Firestore.collectionData(chargedKegsQuery, { "idField": "uid" }).pipe(Rxjs.map((kegs, param) => {
    kegs.sort((a, b) => a.serial - b.serial | 0);
    return kegs;
  }));
}

function usePlacePersonDocumentStatus(options, placeId, personId) {
  let firestore = Reactfire.useFirestore();
  let personRef = placePersonDocument(firestore, placeId, personId);
  return Reactfire.useFirestoreDocData(personRef, options);
}

function useMostRecentKegStatus(placeId) {
  let firestore = Reactfire.useFirestore();
  return Reactfire.useFirestoreCollectionData(Firestore$1.query(placeKegsCollection(firestore, placeId), Firestore$1.orderBy("serial", "desc"), Firestore$1.limit(1)), { "idField": "uid" });
}

async function addConsumption(firestore, consumption, kegId, personId, placeId, tapName) {
  let now = Date.now();
  let kegRef = placeKegDocument(firestore, placeId, kegId);
  let updateKegData = ObjectUtils.setIn({
    recentConsumptionAt: Firestore$1.serverTimestamp()
  }, `consumptions.` + now.toString(), consumption);
  let personsIndexRef = personsIndexDocument(firestore, placeId);
  let personsIndex = (await Firestore$1.getDocFromCache(personsIndexRef)).data({});
  let personRecord = personsAllTupleToRecord(personsIndex.all[personId]);
  let personsIndexUpdateData = {};
  personsIndexUpdateData[`all.` + personId] = personsAllRecordToTuple({
    balance: personRecord.balance,
    name: personRecord.name,
    preferredTap: tapName,
    recentActivityAt: Firestore$1.Timestamp.now(),
    userId: personRecord.userId
  });
  return Firestore$1.writeBatch(firestore).update(kegRef, updateKegData).update(personsIndexRef, personsIndexUpdateData).commit();
}

function $$delete(firestore, placeId, kegId) {
  return Firestore$1.deleteDoc(kegDoc(firestore, placeId, kegId));
}

function deleteConsumption(firestore, placeId, kegId, consumptionId) {
  let kegRef = kegDoc(firestore, placeId, kegId);
  let updateData = ObjectUtils.setIn({}, `consumptions.` + consumptionId, Firestore$1.deleteField());
  return Firestore$1.updateDoc(kegRef, updateData);
}

function finalizeGetUpdateObjects(keg, place, personsIndex) {
  let kegId = getUid(keg);
  let nowTimestamp = Firestore$1.Timestamp.now();
  let kegUpdataObject = {
    depletedAt: nowTimestamp
  };
  let personsUpdateObjects = new Map();
  let personsIndexUpdateObject = {};
  let placeUpdateObject = {};
  let kegOnTap = Object.entries(place.taps).find(param => Stdlib_Option.getOr(Stdlib_Option.map(Primitive_option.fromNull(param[1]), kegRef => kegRef.id === kegId), false));
  if (kegOnTap !== undefined) {
    placeUpdateObject[`taps.` + kegOnTap[0]] = null;
  }
  let donorsEntries = Object.entries(keg.donors);
  let kegDonorPrice = Stdlib_Array.reduce(donorsEntries, 0, (price, param) => price + param[1] | 0);
  let kegPricePerMilliliter = kegDonorPrice / keg.consumptionsSum;
  let personsTransactions = new Map();
  groupKegConsumptionsByUser(undefined, keg).forEach((consumptions, personId) => {
    let personConsumptionSum = Stdlib_Array.reduce(consumptions, 0, (sum, consumption) => sum + consumption.milliliters | 0);
    let priceShare = personConsumptionSum * kegPricePerMilliliter | 0;
    let financialTransaction_amount = -1 * priceShare | 0;
    let financialTransaction_keg = keg.serial;
    let financialTransaction_note = null;
    let financialTransaction_person = null;
    let financialTransaction = {
      amount: financialTransaction_amount,
      createdAt: nowTimestamp,
      keg: financialTransaction_keg,
      note: financialTransaction_note,
      person: financialTransaction_person
    };
    personsTransactions.set(personId, [financialTransaction]);
  });
  Object.entries(keg.donors).forEach(param => {
    let personId = param[0];
    let financialTransaction_amount = param[1];
    let financialTransaction_keg = keg.serial;
    let financialTransaction_note = null;
    let financialTransaction_person = null;
    let financialTransaction = {
      amount: financialTransaction_amount,
      createdAt: nowTimestamp,
      keg: financialTransaction_keg,
      note: financialTransaction_note,
      person: financialTransaction_person
    };
    let transactions = personsTransactions.get(personId);
    if (transactions !== undefined) {
      transactions.push(financialTransaction);
    } else {
      personsTransactions.set(personId, [financialTransaction]);
    }
  });
  personsTransactions.forEach((transactions, personId) => {
    personsUpdateObjects.set(personId, {
      transactions: Firestore$1.arrayUnion(...transactions)
    });
    let transactiuonsSum = Stdlib_Array.reduce(transactions, 0, (sum, transaction) => sum + transaction.amount | 0);
    let personsAllRecord = Stdlib_Option.getOrThrow(personsIndex.all[personId], undefined);
    let newPersonsAllRecord_balance = personsAllRecord.balance + transactiuonsSum | 0;
    let newPersonsAllRecord_name = personsAllRecord.name;
    let newPersonsAllRecord_preferredTap = personsAllRecord.preferredTap;
    let newPersonsAllRecord_recentActivityAt = personsAllRecord.recentActivityAt;
    let newPersonsAllRecord_userId = personsAllRecord.userId;
    let newPersonsAllRecord = {
      balance: newPersonsAllRecord_balance,
      name: newPersonsAllRecord_name,
      preferredTap: newPersonsAllRecord_preferredTap,
      recentActivityAt: newPersonsAllRecord_recentActivityAt,
      userId: newPersonsAllRecord_userId
    };
    personsIndexUpdateObject[`all.` + personId] = personsAllRecordToTuple(newPersonsAllRecord);
  });
  return [
    kegUpdataObject,
    personsUpdateObjects,
    placeUpdateObject,
    personsIndexUpdateObject
  ];
}

async function finalize(firestore, placeId, kegId) {
  let kegRef = kegDoc(firestore, placeId, kegId).withConverter(kegConverter);
  let keg = (await Firestore$1.getDocFromCache(kegRef)).data({});
  let placeRef = placeDocument(firestore, placeId);
  let place = (await Firestore$1.getDocFromCache(placeRef)).data({});
  let personsIndexRef = personsIndexDocument(firestore, placeId);
  let personsIndex = (await Firestore$1.getDocFromCache(personsIndexRef.withConverter(personsIndexConverter))).data({});
  let match = finalizeGetUpdateObjects(keg, place, personsIndex);
  let batch = Firestore$1.writeBatch(firestore);
  match[1].forEach((personUpdateObject, personId) => {
    batch.update(placePersonDocument(firestore, placeId, personId), personUpdateObject);
  });
  return await batch.update(placeRef, match[2]).update(personsIndexRef, match[3]).update(kegRef, match[0]).commit();
}

let Keg = {
  addConsumption: addConsumption,
  $$delete: $$delete,
  deleteConsumption: deleteConsumption,
  finalizeGetUpdateObjects: finalizeGetUpdateObjects,
  finalize: finalize
};

async function add(firestore, personName, placeName, userId) {
  let placeDoc = Firestore$1.doc(Firestore$1.collection(firestore, "places"));
  let personDoc = Firestore$1.doc(placePersonsCollection(firestore, placeDoc.id));
  let personsIndexDoc = personsIndexDocument(firestore, placeDoc.id);
  let defaultTapName = "Pípa";
  let now = Firestore$1.Timestamp.now();
  let personTuple = personsAllRecordToTuple({
    balance: 0,
    name: personName,
    preferredTap: defaultTapName,
    recentActivityAt: now,
    userId: userId
  });
  try {
    await Firestore$1.writeBatch(firestore).set(placeDoc, {
      accounts: Object.fromEntries([[
          userId,
          [
            100,
            0
          ]
        ]]),
      consumptionSymbols: null,
      createdAt: now,
      currency: "CZK",
      name: placeName,
      taps: Object.fromEntries([[
          defaultTapName,
          null
        ]])
    }, {}).set(personDoc, {
      createdAt: now,
      transactions: []
    }, {}).set(personsIndexDoc, {
      all: Object.fromEntries([[
          personDoc.id,
          personTuple
        ]])
    }, {}).commit();
  } catch (raw_e) {
    let e = Primitive_exceptions.internalToException(raw_e);
    if (e.RE_EXN_ID === "JsExn") {
      let e$1 = e._1;
      LogUtils.captureException(e$1);
      throw Primitive_exceptions.internalToException(e$1);
    }
    throw e;
  }
  return placeDoc;
}

function $$delete$1(firestore, placeId) {
  return Firestore$1.deleteDoc(placeDocument(firestore, placeId));
}

async function changePersonRole(firestore, placeId, personId, role) {
  let personsIndexRef = personsIndexDocument(firestore, placeId);
  let personsIndex = (await Firestore$1.getDocFromCache(personsIndexRef)).data({});
  let personTuple = Stdlib_Option.getOrThrow(personsIndex.all[personId], undefined);
  let personRecord = personsAllTupleToRecord(personTuple);
  let personUserId = Stdlib_Option.getOrThrow(Primitive_option.fromNull(personRecord.userId), undefined);
  let placeRef = placeDocument(firestore, placeId);
  let place = (await Firestore$1.getDocFromCache(placeRef)).data({});
  let match = place.accounts[personUserId];
  let updateObject = {};
  updateObject[`accounts.` + personUserId] = [
    role,
    match[1]
  ];
  return await Firestore$1.updateDoc(placeRef, updateObject);
}

function tapAdd(firestore, placeId, tapName) {
  let placeRef = placeDocument(firestore, placeId);
  let updateObject = {};
  updateObject[`taps.` + tapName] = null;
  return Firestore$1.updateDoc(placeRef, updateObject);
}

function tapDelete(firestore, placeId, tapName) {
  let placeRef = placeDocument(firestore, placeId);
  let updateObject = {};
  updateObject[`taps.` + tapName] = Firestore$1.deleteField();
  return Firestore$1.updateDoc(placeRef, updateObject);
}

async function tapRename(firestore, placeId, currentName, newName) {
  let personsIndexRef = personsIndexConverted(firestore, placeId);
  let personsIndex = (await Firestore$1.getDocFromCache(personsIndexRef)).data({});
  let placeRef = placeDocument(firestore, placeId);
  let place = (await Firestore$1.getDocFromCache(placeRef)).data({});
  let currentValue = place.taps[currentName];
  let placeUpdateObject = {};
  let personsIndexUpdateObject = {};
  let newTaps = Object.assign({}, place.taps);
  Stdlib_Dict.$$delete(newTaps, currentName);
  newTaps[newName] = currentValue;
  placeUpdateObject["taps"] = newTaps;
  Object.entries(personsIndex.all).forEach(param => {
    let person = param[1];
    let preferredTap = person.preferredTap;
    if (preferredTap !== undefined && preferredTap === currentName) {
      personsIndexUpdateObject[`all.` + param[0]] = personsAllRecordToTuple({
        balance: person.balance,
        name: person.name,
        preferredTap: newName,
        recentActivityAt: person.recentActivityAt,
        userId: person.userId
      });
      return;
    }
  });
  return await Firestore$1.writeBatch(firestore).update(placeRef, placeUpdateObject).update(personsIndexRef, personsIndexUpdateObject).commit();
}

function tapKegOff(firestore, placeId, tapName) {
  let placeRef = placeDocument(firestore, placeId);
  let updateObject = {};
  updateObject[`taps.` + tapName] = null;
  return Firestore$1.updateDoc(placeRef, updateObject);
}

function tapKegOn(firestore, placeId, tapName, kegId) {
  let placeRef = placeDocument(firestore, placeId);
  let kegRef = kegDoc(firestore, placeId, kegId);
  let updateObject = {};
  updateObject[`taps.` + tapName] = kegRef;
  return Firestore$1.updateDoc(placeRef, updateObject);
}

function update(firestore, placeId, createdAt, name) {
  let placeDoc = placeDocument(firestore, placeId);
  return Firestore$1.updateDoc(placeDoc, {
    createdAt: createdAt,
    name: name
  });
}

async function updateNotificationSubscription(firestore, personUserId, newSubscription, placeId) {
  let placeRef = placeDocument(firestore, placeId);
  let place = (await Firestore$1.getDocFromCache(placeRef)).data({});
  let match = place.accounts[personUserId];
  let updateObject = {};
  updateObject[`accounts.` + personUserId] = [
    match[0],
    newSubscription
  ];
  return await Firestore$1.updateDoc(placeRef, updateObject);
}

let Place = {
  add: add,
  $$delete: $$delete$1,
  changePersonRole: changePersonRole,
  tapAdd: tapAdd,
  tapDelete: tapDelete,
  tapRename: tapRename,
  tapKegOff: tapKegOff,
  tapKegOn: tapKegOn,
  update: update,
  updateNotificationSubscription: updateNotificationSubscription
};

async function add$1(firestore, placeId, personName) {
  let placeSnapshot = await Firestore$1.getDocFromCache(placeDocument(firestore, placeId));
  let place = placeSnapshot.data({});
  let firstTap = Stdlib_Option.getOrThrow(Object.keys(place.taps)[0], undefined);
  let now = Firestore$1.Timestamp.now();
  let personDoc = Firestore$1.doc(placePersonsCollection(firestore, placeId));
  let newPerson_transactions = [];
  let newPerson = {
    createdAt: now,
    transactions: newPerson_transactions
  };
  let newPersonsAllRecord_preferredTap = firstTap;
  let newPersonsAllRecord_userId = null;
  let newPersonsAllRecord = {
    balance: 0,
    name: personName,
    preferredTap: newPersonsAllRecord_preferredTap,
    recentActivityAt: now,
    userId: newPersonsAllRecord_userId
  };
  let updatePersonsIndexData = ObjectUtils.setIn({}, `all.` + personDoc.id, personsAllRecordToTuple(newPersonsAllRecord));
  return await Firestore$1.writeBatch(firestore).set(personDoc, newPerson, {}).update(personsIndexDocument(firestore, placeId), updatePersonsIndexData).commit();
}

async function addFinancialTransaction(firestore, placeId, personId, counterPartyId, transaction) {
  let personsIndexRef = personsIndexDocument(firestore, placeId);
  let personsIndex = (await Firestore$1.getDocFromCache(personsIndexRef)).data({});
  let personsAllTuple = Stdlib_Option.getOrThrow(personsIndex.all[personId], undefined);
  let personsAllRecord = personsAllTupleToRecord(personsAllTuple);
  let newPersonsAllRecord_balance = personsAllRecord.balance + transaction.amount | 0;
  let newPersonsAllRecord_name = personsAllRecord.name;
  let newPersonsAllRecord_preferredTap = personsAllRecord.preferredTap;
  let newPersonsAllRecord_recentActivityAt = personsAllRecord.recentActivityAt;
  let newPersonsAllRecord_userId = personsAllRecord.userId;
  let newPersonsAllRecord = {
    balance: newPersonsAllRecord_balance,
    name: newPersonsAllRecord_name,
    preferredTap: newPersonsAllRecord_preferredTap,
    recentActivityAt: newPersonsAllRecord_recentActivityAt,
    userId: newPersonsAllRecord_userId
  };
  let updatePersonsIndexData = {};
  updatePersonsIndexData[`all.` + personId] = personsAllRecordToTuple(newPersonsAllRecord);
  let counterPartyTuple = Stdlib_Option.getOrThrow(personsIndex.all[counterPartyId], undefined);
  let counterPartyRecord = personsAllTupleToRecord(counterPartyTuple);
  let newCounterPartyRecord_balance = counterPartyRecord.balance - transaction.amount | 0;
  let newCounterPartyRecord_name = counterPartyRecord.name;
  let newCounterPartyRecord_preferredTap = counterPartyRecord.preferredTap;
  let newCounterPartyRecord_recentActivityAt = counterPartyRecord.recentActivityAt;
  let newCounterPartyRecord_userId = counterPartyRecord.userId;
  let newCounterPartyRecord = {
    balance: newCounterPartyRecord_balance,
    name: newCounterPartyRecord_name,
    preferredTap: newCounterPartyRecord_preferredTap,
    recentActivityAt: newCounterPartyRecord_recentActivityAt,
    userId: newCounterPartyRecord_userId
  };
  updatePersonsIndexData[`all.` + counterPartyId] = personsAllRecordToTuple(newCounterPartyRecord);
  let counterPartyTransaction_amount = -1 * transaction.amount | 0;
  let counterPartyTransaction_createdAt = transaction.createdAt;
  let counterPartyTransaction_keg = null;
  let counterPartyTransaction_note = transaction.note;
  let counterPartyTransaction = {
    amount: counterPartyTransaction_amount,
    createdAt: counterPartyTransaction_createdAt,
    keg: counterPartyTransaction_keg,
    note: counterPartyTransaction_note,
    person: personId
  };
  return await Firestore$1.writeBatch(firestore).update(placePersonDocument(firestore, placeId, personId), {
    transactions: Firestore$1.arrayUnion(transaction)
  }).update(placePersonDocument(firestore, placeId, counterPartyId), {
    transactions: Firestore$1.arrayUnion(counterPartyTransaction)
  }).update(personsIndexRef, updatePersonsIndexData).commit();
}

async function $$delete$2(firestore, placeId, personId) {
  let personRef = placePersonDocument(firestore, placeId, personId);
  let personsIndexRef = personsIndexDocument(firestore, placeId);
  let personsIndex = (await Firestore$1.getDocFromCache(personsIndexRef)).data({});
  let personTuple = Stdlib_Option.getOrThrow(personsIndex.all[personId], undefined);
  let personRecord = personsAllTupleToRecord(personTuple);
  let updatePersonIndexData = {};
  updatePersonIndexData[`all.` + personId] = Firestore$1.deleteField();
  let writeBatch = Firestore$1.writeBatch(firestore).delete(personRef).update(personsIndexRef, updatePersonIndexData);
  let userId = personRecord.userId;
  if (userId !== null) {
    let placeRef = placeDocument(firestore, placeId);
    let updatePlaceData = {};
    updatePlaceData[`users.` + userId] = Firestore$1.deleteField();
    writeBatch.update(placeRef, updatePlaceData);
  }
  return await writeBatch.commit();
}

let Person = {
  add: add$1,
  addFinancialTransaction: addFinancialTransaction,
  $$delete: $$delete$2
};

function allEntriesSortedRx(firestore, placeId) {
  let personsIndexRef = personsIndexConverted(firestore, placeId);
  return Firestore.docData(personsIndexRef, { "idField": "uid" }).pipe(Rxjs.map((maybePersonsIndex, param) => {
    if (maybePersonsIndex === undefined) {
      return [];
    }
    let personsAllEntries = Object.entries(maybePersonsIndex.all);
    personsAllEntries.sort((param, param$1) => param[1].name.localeCompare(param$1[1].name));
    return personsAllEntries;
  }));
}

function update$1(firestore, placeId, personsChanges) {
  let personsIndexRef = personsIndexDocument(firestore, placeId);
  let updateObject = {};
  personsChanges.forEach(param => {
    updateObject[`all.` + param[0]] = personsAllRecordToTuple(param[1]);
  });
  return Firestore$1.updateDoc(personsIndexRef, updateObject);
}

let PersonsIndex = {
  allEntriesSortedRx: allEntriesSortedRx,
  update: update$1
};

function collection(firestore) {
  return Firestore$1.collection(firestore, "shareLinks");
}

function document(firestore, linkId) {
  return Firestore$1.doc(firestore, `shareLinks/` + linkId);
}

function $$delete$3(firestore, linkId) {
  return Firestore$1.deleteDoc(document(firestore, linkId));
}

async function upsert(firestore, placeId, personId, role) {
  if (role === 100) {
    Stdlib_JsError.throwWithMessage("Changing owner is currently not supported");
  }
  let shareLinkCollection = Firestore$1.collection(firestore, "shareLinks");
  let shareLinkQuery = Firestore$1.query(shareLinkCollection, Firestore$1.where("person", "==", personId), Firestore$1.where("place", "==", placeId), Firestore$1.limit(1));
  let shareLinks = await Firestore$1.getDocs(shareLinkQuery);
  let match = shareLinks.docs;
  if (match.length !== 1) {
    return (await Firestore$1.addDoc(shareLinkCollection, {
      createdAt: Firestore$1.Timestamp.now(),
      person: personId,
      place: placeId,
      role: role
    })).id;
  }
  let shareLinkSnapshot = match[0];
  await Firestore$1.updateDoc(shareLinkSnapshot.ref, {
    createdAt: Firestore$1.Timestamp.now(),
    role: role
  });
  return shareLinkSnapshot.id;
}

function acceptInvitation(firestore, linkId, userId) {
  let shareLinkDocument = document(firestore, linkId);
  return Firestore$1.runTransaction(firestore, async transaction => {
    let shareLinkSnapshot = await transaction.get(shareLinkDocument);
    if (!shareLinkSnapshot.exists()) {
      Stdlib_JsError.throwWithMessage("Share link does not exist");
    }
    let match = shareLinkSnapshot.data({});
    let place = match.place;
    let person = match.person;
    let placeIndexDocument = personsIndexConverted(firestore, place);
    let placeIndex = (await transaction.get(placeIndexDocument)).data({});
    let userAlreadyInPlace = Object.values(placeIndex.all).some(p => Stdlib_Null.mapOr(p.userId, false, id => id === userId));
    if (userAlreadyInPlace) {
      Stdlib_JsError.throwWithMessage("User already in place");
    }
    let personRecord = Stdlib_Option.getOrThrow(placeIndex.all[person], undefined);
    if (personRecord.userId !== null) {
      Stdlib_JsError.throwWithMessage("Person already has a connected user account");
    }
    let newPersonRecord_balance = personRecord.balance;
    let newPersonRecord_name = personRecord.name;
    let newPersonRecord_preferredTap = personRecord.preferredTap;
    let newPersonRecord_recentActivityAt = personRecord.recentActivityAt;
    let newPersonRecord = {
      balance: newPersonRecord_balance,
      name: newPersonRecord_name,
      preferredTap: newPersonRecord_preferredTap,
      recentActivityAt: newPersonRecord_recentActivityAt,
      userId: userId
    };
    let personsIndexUpdateData = {};
    personsIndexUpdateData[`all.` + person] = personsAllRecordToTuple(newPersonRecord);
    let placeUpdateData = {};
    placeUpdateData[`accounts.` + userId] = [
      match.role,
      0
    ];
    transaction.update(placeDocument(firestore, place), placeUpdateData);
    transaction.update(placeIndexDocument, personsIndexUpdateData);
    transaction.delete(shareLinkDocument);
  });
}

let ShareLink = {
  collection: collection,
  document: document,
  $$delete: $$delete$3,
  upsert: upsert,
  acceptInvitation: acceptInvitation
};

let slidingWindowInHours = 12;

export {
  kegDoc,
  placeCollection,
  personsIndexDocument,
  placeDocument,
  placePersonsCollection,
  placePersonDocument,
  placeKegsCollection,
  placeKegDocument,
  webAuthnUsersDocument,
  removeLastUndefined,
  personsAllRecordToTuple,
  personsAllTupleToRecord,
  isPersonActive,
  personsIndexConverter,
  personsIndexConverted,
  formatKegSerial,
  kegConverter,
  placeKegsCollectionConverted,
  getUid,
  placesByUserIdRx,
  slidingWindowInHours,
  slidingWindowInMillis,
  slidingWindowRx,
  recentlyFinishedKegsRx,
  kegFirstConsumptionTimestamp,
  groupKegConsumptionsByUser,
  allChargedKegsRx,
  usePlacePersonDocumentStatus,
  useMostRecentKegStatus,
  Keg,
  Place,
  Person,
  PersonsIndex,
  ShareLink,
}
/* slidingWindowRx Not a pure module */
